import React, { useState, useEffect } from "react";
import { plusIcon, Pagination, Modal } from "../../Components";
import { AnimatePresence } from "framer-motion";
import GalleryGridItem from "./GalleryGridItem";

export default function GalleryMain({
  setTab,
  paginationData,
  onPaginate,
  galleryList,
  isLoaded,
  search,
  searchText,
  updateStatusImage,
  deleteImage,
}) {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmAction, setShowConfirmAction] = useState(false);
  const [action, setAction] = useState("");
  const [idForAction, setIdForAction] = useState("");

  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === "Enter") {
        if (searchText !== "") {
          search();
        }
      }
    };
    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [searchText]);

  const confirmDelete = (id) => {
    setIdForAction(id);
    setShowConfirmDelete(true);
  };

  const confirmShow = (id) => {
    setAction("visible");
    setIdForAction(id);
    setShowConfirmAction(true);
  };

  const confirmHide = (id) => {
    setIdForAction(id);
    setAction("hide");
    setShowConfirmAction(true);
  };

  const deleteSelImage = (id) => {
    setShowConfirmDelete(false);
    deleteImage(id);
  };

  const actionImage = (id) => {
    setShowConfirmAction(false);
    updateStatusImage(id, action);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          {/* <h1 className="text-4xl font-bold text-emerald-900 mb-4 font-serif">Visual Gallery</h1> */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border-l-4 border-emerald-500">
            <p className="text-slate-700 text-lg leading-relaxed">
              Showcase your best content to potential advertisers. Upload your
              images to get noticed and collaborate with brands. Control which
              images are visible to advertisers. Toggle visibility on each image
              to manage your showcase.
            </p>
          </div>
        </div>

        {/* Controls Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border border-slate-200">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <button
                className="flex items-center gap-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 hover:shadow-md"
                onClick={() => setTab("add")}
              >
                {plusIcon}
                <span>Upload New</span>
              </button>
              <span className="text-slate-500 text-sm">
                (maximum 100 images)
              </span>
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="">
          {isLoaded && galleryList.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {galleryList.map((item, index) => {
                return (
                  <GalleryGridItem
                    confirmHide={confirmHide}
                    confirmShow={confirmShow}
                    confirmDelete={confirmDelete}
                    setIdForAction={setIdForAction}
                    key={index}
                    {...item}
                  />
                );
              })}
            </div>
          ) : (
            isLoaded && (
              <div className="text-center py-20">
                <div className="bg-white rounded-xl shadow-sm p-12 max-w-md mx-auto border border-slate-200">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl">📸</span>
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-800 mb-3">
                    No images yet
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Start building your gallery by uploading your first image
                  </p>
                  <button
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 px-6 rounded-lg transition-colors"
                    onClick={() => setTab("add")}
                  >
                    Upload First Image
                  </button>
                </div>
              </div>
            )
          )}
        </div>

        {/* Pagination */}
        <div className="assets__table-footer mt-8">
          {isLoaded && galleryList.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
              <Pagination
                perPage={paginationData["per_page"]}
                total={paginationData["total"]}
                currentPage={paginationData["current_page"]}
                totalPages={paginationData["total_pages"]}
                onPaginate={onPaginate}
              />
            </div>
          )}
        </div>

        {/* Modals */}
        <AnimatePresence>
          {showConfirmDelete === true && (
            <Modal addClass="modalAsset">
              <div className="bg-white rounded-xl shadow-xl p-8 max-w-md mx-auto">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      ></path>
                    </svg>
                  </div>
                  <h6 className="text-xl font-bold text-slate-800 mb-2">
                    Delete Image?
                  </h6>
                  <p className="text-slate-600">
                    This image will be permanently removed from your gallery.
                    This action cannot be undone.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium py-3 px-6 rounded-lg transition-colors"
                    onClick={() => setShowConfirmDelete(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
                    onClick={() => deleteSelImage(idForAction)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </Modal>
          )}

          {showConfirmAction === true && (
            <Modal addClass="modalAsset">
              <div className="bg-white rounded-xl shadow-xl p-8 max-w-md mx-auto">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-emerald-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      ></path>
                    </svg>
                  </div>
                  <h6 className="text-xl font-bold text-slate-800 mb-2">
                    {action === "hide" ? "Hide from Gallery?" : "Make Visible?"}
                  </h6>
                  <p className="text-slate-600">
                    {action === "hide"
                      ? "This image will be hidden from advertisers but remain in your gallery."
                      : "This image will be visible to advertisers in your gallery."}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium py-3 px-6 rounded-lg transition-colors"
                    onClick={() => setShowConfirmAction(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
                    onClick={() => actionImage(idForAction)}
                  >
                    {action === "hide" ? "Hide Image" : "Make Visible"}
                  </button>
                </div>
              </div>
            </Modal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
